import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useMedplum } from "@medplum/react";

import { useDisclosure } from "@mantine/hooks";

import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { EncounterProceduresScope } from "../constants/procedures";
import useProceduresStaging from "../hooks/useProceduresStaging";
import { getEncounterProceduresAndConditions } from "../services/procedures";
import { StagedProceduresOperation } from "../types/procedures";
import { EncounterProceduresData } from "../utils/procedures";

/**
 * The encounter's clinical record: everything the procedures picker wrote, minus the biller section's
 * own codes. Every section that shows the encounter's diagnoses reads this same scope, which is what
 * lets them agree about what the encounter holds.
 */
const ENCOUNTER_PROCEDURES_SCOPE = EncounterProceduresScope.Clinical;

export type EncounterProceduresRead = {
  isLoading: boolean;
  /**
   * The encounter as its save is going to leave it: what the last read returned with the staged work
   * replayed on top. Kept as it is when a re-read of the same encounter fails, rather than blanked,
   * and dropped when the encounter changes.
   */
  data: EncounterProceduresData | undefined;
  /** True when the read failed, so an outage is not shown as an encounter with nothing on it. */
  hasLoadFailed: boolean;
  /** True while a section has staged work the encounter has not written yet. */
  hasStagedChanges: boolean;
  /** Stages a finished picker session or a removal onto the encounter's next save. */
  stageOperation: (operation: StagedProceduresOperation) => void;
  /** Re-reads the encounter. Resolves once that read has landed, so a save can wait for it. */
  reload: () => Promise<void>;
};

const EncounterProceduresContext = createContext<EncounterProceduresRead | null>(null);

/**
 * The encounter's clinical Procedures and Conditions, with everything staged onto its next save.
 *
 * Deliberately not optional: both sections that read this are rendered inside a tab shell that
 * provides it, and a silent fallback to a private read is what let the two disagree before.
 */
export function useEncounterProcedures(): EncounterProceduresRead {
  const value = useContext(EncounterProceduresContext);

  if (!value) {
    throw new Error("useEncounterProcedures must be used inside an EncounterProceduresProvider");
  }

  return value;
}

type EncounterProceduresProviderProps = {
  clientId: string | undefined;
  encounterId: string | undefined;
  children: ReactNode;
};

/**
 * Owns the one read of the encounter's clinical Procedures and Conditions, and the one staged set
 * written on top of it.
 *
 * Both the procedures tab and the Dx tab's Diagnoses section show these resources, and Mantine keeps
 * every `Tabs.Panel` mounted, so before this each tab issued the same pair of uncached searches on
 * page load and then drifted: only the tab that wrote re-read, and the other kept showing the
 * encounter as it looked when the page opened.
 *
 * The staging is owned here for the same reason. It registers the encounter's child save and the
 * navigation blocker's unsaved check, both of which are one per encounter, so a section calling it
 * for itself would stage into a set the other sections neither preview nor warn about. `data` is
 * therefore the previewed encounter — there is no unstaged read to reach for by mistake — and the
 * re-read after a write reaches every subscriber.
 */
export function EncounterProceduresProvider({
  clientId,
  encounterId,
  children,
}: EncounterProceduresProviderProps) {
  const medplum = useMedplum();
  const [isLoading, { open: startLoading, close: stopLoading }] = useDisclosure(true);
  const [serverData, setServerData] = useState<EncounterProceduresData>();
  const [hasLoadFailed, setHasLoadFailed] = useState(false);

  // Only the newest read may write to state. Moving between encounters can leave an earlier read in
  // flight, and the slower of the two must not replace what the newer one found.
  const readIdRef = useRef(0);

  // The one read, awaitable by whoever asked for it: the encounter's save re-reads through this and
  // waits for the result, so the cards are the saved encounter by the time the save reports done.
  const read = useCallback(async () => {
    readIdRef.current += 1;
    const readId = readIdRef.current;
    const isStale = () => readIdRef.current !== readId;

    // Both ids come from the encounter's own route, so this is a broken page rather than a state a
    // provider reaches. Reported as a failed read — an empty result here would read as an encounter
    // with nothing recorded on it — and with nothing left on screen, because whatever is in state
    // was read for an encounter this render can no longer name.
    if (!clientId || !encounterId) {
      setServerData(undefined);
      setHasLoadFailed(true);
      stopLoading();
      return;
    }

    try {
      const result = await getEncounterProceduresAndConditions({
        medplum,
        clientId,
        encounterId,
        scope: ENCOUNTER_PROCEDURES_SCOPE,
      });
      if (isStale()) return;

      setServerData(result);
      setHasLoadFailed(false);
    } catch (error) {
      if (isStale()) return;

      // Only the encounter's id reaches Sentry. What the encounter was diagnosed with is clinical;
      // a failure to display it is an operational problem.
      captureExceptionWithoutPhi(
        "Failed to read the encounter's procedures and conditions",
        error,
        { encounterId },
      );
      setHasLoadFailed(true);
    } finally {
      if (!isStale()) stopLoading();
    }
  }, [clientId, encounterId, medplum, stopLoading]);

  // A different encounter reads from scratch: behind the sections' loading state, and with the
  // previous encounter's record dropped, so a read that fails cannot leave one encounter's
  // diagnoses standing under another encounter's heading. A reload does neither: it runs while the
  // encounter is saving, and blanking the cards mid-save would read as data loss.
  useEffect(() => {
    startLoading();
    setServerData(undefined);
    setHasLoadFailed(false);
    void read();

    // Anything still in flight was read for the encounter being left behind.
    return () => {
      readIdRef.current += 1;
    };
  }, [read, startLoading]);

  const { data, hasStagedChanges, stageOperation } = useProceduresStaging({
    clientId,
    encounterId,
    serverData,
    reload: read,
  });

  const value = useMemo(
    () => ({ isLoading, data, hasLoadFailed, hasStagedChanges, stageOperation, reload: read }),
    [isLoading, data, hasLoadFailed, hasStagedChanges, stageOperation, read],
  );

  return (
    <EncounterProceduresContext.Provider value={value}>
      {children}
    </EncounterProceduresContext.Provider>
  );
}
